import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { RoundedIconButton } from "@/components/ui/rounded-icon-button";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface InitializeVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export const InitializeVisitDialog = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  isLoading = false,
}: InitializeVisitDialogProps) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onCancel?.();
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={isLoading ? undefined : onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[50vh] [&>button]:hidden rounded-t-xl"
      >
        <SheetHeader className="relative p-4 pb-2">
          <div className="flex flex-row items-center justify-between">
            <SheetTitle className="text-lg">
              {t("contractorVisits.initializeVisit.dialog.title")}
            </SheetTitle>
            <RoundedIconButton
              variant="outline"
              size="md"
              onClick={() => !isLoading && onOpenChange(false)}
              className="border-2"
              disabled={isLoading}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">
                {t("contractorVisits.initializeVisit.dialog.close")}
              </span>
            </RoundedIconButton>
          </div>
        </SheetHeader>

        <div className="p-4 pt-6">
          <p className="text-sm text-muted-foreground">
            {t("contractorVisits.initializeVisit.dialog.description")}
          </p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-background border-t p-4 pb-6 space-y-2">
          <Button
            variant="default"
            size="lg"
            className="w-full h-12 text-base font-medium"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? t("contractorVisits.initializeVisit.dialog.loading")
              : t("contractorVisits.initializeVisit.dialog.confirm")}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            className="w-full h-12 text-base"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("contractorVisits.initializeVisit.dialog.cancel")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
